import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CONTACT_EMAIL } from "./utils/contact";
import ProfileCard from "./ProfileCard";
import { Section } from "./ui/Section";
import { ActionList, type ActionListItem } from "./ui/Cards";
import { EmptyState, LoadingState } from "./ui/AsyncState";
import { useAuth } from "./utils/useAuth";
import { useCurrentUser } from "./utils/useCurrentUser";
import { useUserCategory } from "./utils/getUserCategory";
import { useSignOut } from "./utils/useSignOut";

/**
 * Profil (/profile): karta „Moje údaje" (ProfileCard) a nabídka účtu.
 * Titulek dodává AppShell (Screen v App.tsx), nepřihlášené odchytí
 * RequireAuth – stránka proto nic nepřesměrovává.
 *
 * Nabídka se vykreslí hned, nezávisle na načtení profilu: i když se dokument
 * users/{uid} nenačte, jde se odhlásit nebo otevřít nastavení (dřív visela
 * celá stránka na „Načítám údaje…"). Odhlášení (potvrzení, neodeslané
 * zápisy, vymazání cache) je sdílené s účtem v hlavičce – utils/useSignOut.
 */

const PROFILE_TITLE = "Moje údaje";
const PROFILE_ICON = "bi-person-vcard";

/**
 * Karta s údaji. Vlastní komponenta kvůli „Zkusit znovu": rodič ji přemountuje
 * (key), useCurrentUser se tím přihlásí znovu a dokument načte znovu.
 */
const ProfileDetails: React.FC<{ onRetry: () => void }> = ({ onRetry }) => {
  const navigate = useNavigate();
  const { user, userDoc, loading, missing } = useCurrentUser();
  const category = useUserCategory(userDoc);

  if (loading) {
    return (
      <Section title={PROFILE_TITLE} icon={PROFILE_ICON}>
        <LoadingState text="Načítám údaje…" />
      </Section>
    );
  }

  if (!userDoc && missing) {
    // Dokument users/{uid} neexistuje – Zkusit znovu nepomůže. Registraci
    // (dotaz „Jste to vy?“, doplnění údajů) nebo nedokončené smazání účtu
    // dokončí přihlašovací stránka (AuthPage); stejný text jako Úprava
    // profilu, Členství a Přihláška (nález 6 3. kola).
    return (
      <Section title={PROFILE_TITLE} icon={PROFILE_ICON}>
        <EmptyState
          icon="bi-person-x"
          title="Profil nenalezen"
          text={`K účtu chybí údaje člena – registrace se nedokončila. Dokončete ji tlačítkem níže (je k tomu potřeba připojení k internetu). Pokud jste účet mazali, dokončíte tam i smazání. Když to nepůjde, napište nám na ${CONTACT_EMAIL}.`}
          action={{ label: "Dokončit registraci", onClick: () => navigate("/login") }}
        />
      </Section>
    );
  }

  if (!userDoc) {
    // Čtení dokumentu selhalo (detail zapsal useCurrentUser).
    return (
      <Section title={PROFILE_TITLE} icon={PROFILE_ICON}>
        <EmptyState
          icon="bi-person-exclamation"
          title="Údaje se nepodařilo načíst"
          text={`Zkontrolujte připojení k internetu a zkuste to znovu. Pokud potíže trvají, napište nám na ${CONTACT_EMAIL}.`}
          action={{ label: "Zkusit znovu", onClick: onRetry }}
        />
      </Section>
    );
  }

  return (
    <ProfileCard
      userDoc={userDoc}
      email={user?.email}
      category={category}
      title={PROFILE_TITLE}
    />
  );
};

const UserProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { role } = useAuth();
  const { signOut, signingOut } = useSignOut("UserProfilePage");

  const [attempt, setAttempt] = useState(0);

  // Stejné pravidlo jako tab Admin ve spodní navigaci a guard /admin (RequireRole).
  const isAdmin = role === "admin" || role === "superadmin";

  const handleRetry = useCallback(() => setAttempt((n) => n + 1), []);

  const menuItems: ActionListItem[] = [
    { icon: "bi-pencil-square", label: "Upravit profil", to: "/updateprofile" },
    { icon: "bi-info-circle", label: "O aplikaci", to: "/about" },
    { icon: "bi-shield-lock", label: "Ochrana osobních údajů", to: "/gdpr" },
    ...(isAdmin
      ? [
          {
            icon: "bi-gear",
            label: "Administrace",
            to: "/admin",
            tone: "primary" as const,
          },
        ]
      : []),
  ];

  const logoutItems: ActionListItem[] = [
    {
      icon: "bi-box-arrow-right",
      label: signingOut ? "Odhlašuji…" : "Odhlásit se",
      onClick: signOut,
      tone: "danger",
      chevron: false,
    },
  ];

  return (
    <>
      <ProfileDetails key={attempt} onRetry={handleRetry} />

      <ActionList items={menuItems} onNavigate={navigate} />
      <ActionList items={logoutItems} className="mt-3" />

    </>
  );
};

export default UserProfilePage;
